import { useMemo } from "react";
import { useGetIngredientPackaging, useGetProductsByIngredient } from "../../../../../api/ingredients";
import { Skeleton } from "../../../../../components/ui/common/skeleton";
import { ENABLE_IMPACT_RESULT_DOCUMENTS } from "../../../../../config/constants";
import { ROLES } from "../../../../../config/permission-maps";
import { useIngredientFlags } from "../../../../../hooks/useIngredientFlags";
import { getUserRolesInLocal } from "../../../../../utils/cacheStorage";
import LcaDataSection from "../../../ingredientRegister/components/LcaDataSection";
import PackagingSection from "../../../ingredientRegister/components/PackagingSection";
import TransportationSection from "../../../ingredientRegister/components/TransportationSection";
import { IngredientRowViewModel } from "../../../ingredientList/types/ingredientListView.types";
import EnvironmentalReadOnlyCard from "../summary/EnvironmentalReadOnlyCard";
import ImpactResultDocumentsReadOnly from "../summary/ImpactResultDocumentsReadOnly";
import IngredientOverviewCard from "../summary/IngredientOverviewCard";
import RelatedProductsTable from "../summary/RelatedProductsTable";
import UnitDetailsTable from "../summary/UnitDetailsTable";

interface SummaryTabProps {
  ingredientDetails: IngredientRowViewModel | undefined;
}

/**
 * "Ingredient Summary" tab — Tailwind rebuild of the legacy
 * `IngredientSummaryTab.tsx`. Reuses `PackagingSection`/`TransportationSection`
 * /`LcaDataSection` from the register flow directly in their existing
 * `dataView`/`disabled` (read-only) modes rather than duplicating them — they
 * were already built Tailwind-only and support this exact case.
 */
const SummaryTab = ({ ingredientDetails }: SummaryTabProps) => {
  const localUserRole = getUserRolesInLocal();
  const isUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const ingredientId = ingredientDetails?.ingredient_id as string;
  const supplierId = ingredientDetails?.supplier_id as string;
  const details = ingredientDetails?.supplierIngredientDetails as
    | { ingredient_type?: string; data_type?: string }
    | null
    | undefined;

  const { produceIngredient, processIngredient, lcaIngredient } = useIngredientFlags(
    details?.ingredient_type as any,
    details?.data_type as any
  );

  const enablePackageData = produceIngredient;
  const enableTransportData = processIngredient || (!processIngredient && produceIngredient);
  const enableLCAData = lcaIngredient;

  const { data: ingredientPackageData, isLoading: loadingPackage, refetch: refetchPackagingData } =
    useGetIngredientPackaging({ ingredientId, supplierId, loadPackageData: true });

  const { data: productsByIngredient, isLoading: loadingProducts } = useGetProductsByIngredient({
    ingredientId,
    supplierId
  });

  const isLoading = loadingPackage || loadingProducts;

  const productList = useMemo(
    () => (productsByIngredient as Array<Record<string, unknown>> | undefined) || [],
    [productsByIngredient]
  );

  if (isLoading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} className="tw-h-24 tw-w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <IngredientOverviewCard ingredientDetails={ingredientDetails} />
      <UnitDetailsTable ingredientDetails={ingredientDetails} isUserSupplier={isUserSupplier} />
      <EnvironmentalReadOnlyCard ingredientDetails={ingredientDetails} isUserSupplier={isUserSupplier} />

      <RelatedProductsTable products={productList} />

      {enablePackageData ? (
        <PackagingSection
          ingredientId={ingredientId}
          supplierId={supplierId}
          refetchPackagingData={refetchPackagingData}
          ingredientPackageData={ingredientPackageData}
          dataView
        />
      ) : null}

      {enableTransportData ? (
        <TransportationSection ingredientId={ingredientId} supplierId={supplierId} disabled />
      ) : null}

      {enableLCAData ? (
        <>
          <LcaDataSection ingredientId={ingredientId} supplierId={supplierId} dataView />
          {ENABLE_IMPACT_RESULT_DOCUMENTS && (
            <ImpactResultDocumentsReadOnly ingredientId={ingredientId} supplierId={supplierId} />
          )}
        </>
      ) : null}
    </div>
  );
};

export default SummaryTab;
